import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../store';
import { isUltraAdmin } from '../utils';
import { getAllTemplates, deleteTemplate, saveTemplate } from '../services/firestore';
import type { Event } from '../types';

/**
 * Templates Page (Ultra Admin Only)
 * Manage templates - create, edit, and delete
 */
export function TemplatesPage() {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  // Check ultra-admin access
  useEffect(() => {
    if (!isAuthenticated || !currentUser || !isUltraAdmin(currentUser)) {
      navigate('/');
      return;
    }
  }, [isAuthenticated, currentUser, navigate]);

  // Load all templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const loadedTemplates = await getAllTemplates();
        
        // Sort by creation date (most recent first)
        loadedTemplates.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setTemplates(loadedTemplates);
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && isUltraAdmin(currentUser)) {
      loadTemplates();
    }
  }, [currentUser]);

  const handleDeleteTemplate = async (templateId: string, templateName: string) => {
    const confirmation = window.confirm(
      `Are you sure you want to DELETE the template "${templateName}"? This will permanently delete the template. Events created from this template will NOT be affected.`
    );
    
    if (confirmation) {
      await deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
    }
  };

  const handleToggleAvailability = async (template: Event) => {
    const newAvailability = !template.isAvailableForEvents;
    const updatedTemplate = {
      ...template,
      isAvailableForEvents: newAvailability,
    };
    
    await saveTemplate(updatedTemplate);
    setTemplates(templates.map(t => t.id === template.id ? updatedTemplate : t));
  };

  const handleCreateTemplate = () => {
    navigate('/ultra-admin/templates/create');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || !isUltraAdmin(currentUser)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-primary-text">Templates</h1>
              <p className="text-sm text-secondary-text">Ultra Admin: Manage event templates</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/account')}
            >
              Back
            </Button>
          </div>
          <Button
            variant="primary"
            size="md"
            fullWidth
            onClick={handleCreateTemplate}
          >
            + Create New Template
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {templates.length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <p className="text-secondary-text mb-4">No templates created yet</p>
              <p className="text-sm text-muted-text mb-6">
                Templates are reusable fight cards that users can use to create events
              </p>
              <Button
                variant="primary"
                onClick={handleCreateTemplate}
              >
                Create Your First Template
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {templates.map((template) => (
              <Card key={template.id} padding="md">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-primary-text">{template.name}</h3>
                    <div className="flex gap-2 items-center mt-2">
                      <Badge variant="info" size="sm">
                        Template
                      </Badge>
                      {template.templateUsageCount && template.templateUsageCount > 0 ? (
                        <span className="text-sm text-secondary-text">
                          Used {template.templateUsageCount} time{template.templateUsageCount !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-text">
                          Not used yet
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-tertiary-bg rounded-lg">
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Sport</div>
                    <div className="font-semibold text-primary-text">
                      {template.sport || 'UFC'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Fights</div>
                    <div className="font-semibold text-primary-text">
                      {template.fights.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Created By</div>
                    <div className="font-semibold text-primary-text">
                      {template.adminUsername || 'Unknown'}
                    </div>
                  </div>
                </div>

                <div className="mb-3 p-3 bg-tertiary-bg rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-text">Published:</span>
                    <Badge variant={(template.published ?? true) ? 'success' : 'default'}>
                      {(template.published ?? true) ? 'Published' : 'Draft'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-text">Available for Events:</span>
                    <Button
                      variant={template.isAvailableForEvents === false ? 'secondary' : 'success'}
                      size="sm"
                      onClick={() => handleToggleAvailability(template)}
                    >
                      {template.isAvailableForEvents === false ? 'Unavailable' : 'Available'}
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => navigate(`/admin/${template.id}`)}
                  >
                    Edit Template
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    fullWidth
                    onClick={() => handleDeleteTemplate(template.id, template.name)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}




